import { LvTime } from 'Interfaces/ReferenceInterfaces/LvTimeInterface';
import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import searchIcon from 'assets/icons/PPSIcons/searchIcon.svg';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';
import { commaSeparatedNumbers } from '../../../../utils/commaSeparatedNumbers';
import classes from './lvTimeTable.module.scss';
import { useLvTimeTable } from './useLvTimeTable';
import AddRowModalLvTime from './utils/AddRowModalLVTime';
import DeleteConfirmationModalLvTime from './utils/DeleteConfirmationModalLVTime';

interface SetupTimeTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const LvTimeTable = observer(
  ({ showAddButton = true, showActionColumn = true, showAllRows = true }: SetupTimeTableProps) => {
    const pageSize = 10000;
    const {
      lvTimeData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData,
      incomingJobTypeOptions,
      onPageChange,
      searchQuery,
      handleSearchChange,
      handleExportToExcel,
      roomTypeOptions,
      dosageFormOptions
    } = useLvTimeTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: LvTime) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const roomTemplate = (rowData: LvTime) => {
      return `${rowData.Room_Type.description}`;
    };

    const incomingJobTypeTemplate = (rowData: LvTime) => {
      return rowData.incoming_job_type.description;
    };

    const bottleCountMinBodyTemplate = (rowData: LvTime) => {
      return commaSeparatedNumbers(rowData.bottle_count_min);
    };

    const bottleCountMaxBodyTemplate = (rowData: LvTime) => {
      return commaSeparatedNumbers(rowData.bottle_count_max);
    };

    const lvTimeBodyTemplate = (rowData: LvTime) => {
      return commaSeparatedNumbers(rowData.lv_time_minutes);
    };

    if (isLoading && !lvTimeData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.lvTimeTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>LV Time Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                value={searchQuery}
                className={classes.searchInput}
                placeholder="Search..."
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>

            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />

            {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          value={lvTimeData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? lvTimeData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="Room" header="Room Type" body={roomTemplate} />
          <Column
            field="bottle_count_min"
            header="Bottle Count Min"
            body={bottleCountMinBodyTemplate}
          />
          <Column
            field="bottle_count_max"
            header="Bottle Count Max"
            body={bottleCountMaxBodyTemplate}
          />
          <Column field="dosage_form" header="Dosage Form" />
          <Column
            field="incoming_job_type"
            header="Incoming Job Type"
            body={incomingJobTypeTemplate}
          />
          <Column field="lv_time_minutes" header="LV Time (mins)" body={lvTimeBodyTemplate} />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalLvTime
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
          roomOptions={roomTypeOptions}
          incomingJobTypeOptions={incomingJobTypeOptions}
          dosageFormOptions={dosageFormOptions}
        />

        <DeleteConfirmationModalLvTime
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default LvTimeTable;
